import type { Transaction } from '@/types/treasury';import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuthStore } from '@/stores/authStore';
import { useCreateTransaction, useUpdateTransaction, useAccounts } from '@/hooks/queries/useTreasury';
import { useClients } from '@/hooks/queries/useClients';
import { TRANSACTION_CATEGORIES, CATEGORY_LABELS } from '@/types/treasury';
import { toast } from '@/components/ui/toast/useToast';
import { Loader2, Plus, ExternalLink } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';

const transactionSchema = z.object({
  account_id: z.string().min(1, 'Compte requis'),
  type: z.enum(['income', 'expense']),
  amount: z.number().min(0.01, 'Montant requis'),
  payment_method: z.enum(['cash', 'check', 'transfer', 'card', 'mobile', 'ccp']),
  transaction_date: z.string(),
  category: z.string().optional(),
  description: z.string().min(1, 'Description requise'),
  
  // Check fields
  check_number: z.string().optional(),
  check_date: z.string().optional(),
  check_bank: z.string().optional(),
  check_drawer_name: z.string().optional(),
  
  // Contact fields
  contact_id: z.string().optional(),
  contact_name: z.string().optional(),
  
  notes: z.string().optional(),
});

type TransactionFormData = z.infer<typeof transactionSchema>;

interface TransactionFormProps {
  open: boolean;
  onClose: () => void;
  transaction?: Transaction | null; // If provided, edit mode
}

// Store form state in sessionStorage for persistence
const FORM_CACHE_KEY = 'treasury_transaction_form_cache';

export function TransactionForm({ open, onClose, transaction }: TransactionFormProps) {
  const navigate = useNavigate();
  const { company, user } = useAuthStore();
  const { data: accounts = [] } = useAccounts(company?.id || '');
  const createTransaction = useCreateTransaction();
  const updateTransaction = useUpdateTransaction();
  const isEditMode = !!transaction;
  const [showClientSelect, setShowClientSelect] = useState(false);

  const form = useForm<TransactionFormData>({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      type: 'income',
      transaction_date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      amount: 0,
    },
  });

  const transactionType = form.watch('type');
  
  // Filter clients/suppliers based on transaction type
  const { data: clients = [] } = useClients(
    company?.id || '',
    transactionType === 'income' ? 'client' : 'supplier'
  );

  // Load transaction data for edit mode or cached data for create mode
  useEffect(() => {
    if (open) {
      if (transaction) {
        // Edit mode: load transaction data
        form.reset({
          account_id: transaction.account_id,
          type: transaction.type as 'income' | 'expense',
          amount: transaction.amount,
          payment_method: transaction.payment_method,
          transaction_date: transaction.transaction_date,
          category: transaction.category,
          description: transaction.description,
          check_number: transaction.check_number || '',
          check_date: transaction.check_date || '',
          check_bank: transaction.check_bank || '',
          check_drawer_name: transaction.check_drawer_name || '',
          contact_id: transaction.contact_id || '',
          contact_name: transaction.contact_name || '',
          notes: transaction.notes || '',
        });
      } else {
        // Create mode: load cached data
        const cached = sessionStorage.getItem(FORM_CACHE_KEY);
        if (cached) {
          try {
            const data = JSON.parse(cached);
            form.reset(data);
          } catch (e) {
            console.error('Failed to load cached form data');
          }
        }
      }
    }
  }, [open, transaction]);

  // Save form data to cache only in create mode
  useEffect(() => {
    if (open && !isEditMode) {
      const subscription = form.watch((data) => {
        sessionStorage.setItem(FORM_CACHE_KEY, JSON.stringify(data));
      });
      return () => subscription.unsubscribe();
    }
  }, [open, isEditMode, form]);

  const paymentMethod = form.watch('payment_method');
  const contactId = form.watch('contact_id');
  const selectedAccountId = form.watch('account_id');
  const amount = form.watch('amount');
  
  // Calculate balance preview
  const selectedAccount = accounts.find(a => a.id === selectedAccountId);
  const balancePreview = selectedAccount ? (
    isEditMode ? (
      // Edit mode: reverse old, apply new
      selectedAccount.current_balance +
      (transaction.type === 'income' ? -transaction.amount : transaction.amount) +
      (transactionType === 'income' ? amount : -amount)
    ) : (
      // Create mode: apply new
      transactionType === 'income'
        ? selectedAccount.current_balance + (amount || 0)
        : selectedAccount.current_balance - (amount || 0)
    )
  ) : 0;
  
  // Generate transaction number
  const generateTransactionNumber = () => {
    const prefix = transactionType === 'income' ? 'REC' : 'PAY';
    const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}-${date}-${random}`;
  };

  const onSubmit = async (data: TransactionFormData) => {
    try {
      // Find selected client to get name
      let contactName = data.contact_name;
      if (data.contact_id) {
        const client = clients.find(c => c.id === data.contact_id);
        if (client) {
          contactName = client.name;
        }
      }

      // Get current account balance
      const selectedAccount = accounts.find(a => a.id === data.account_id);
      if (!selectedAccount) {
        toast.error('Erreur', 'Compte introuvable.');
        return;
      }

      // Calculate balance impact
      let balanceAfter = selectedAccount.current_balance;
      
      if (isEditMode) {
        // For edit: reverse old transaction, apply new
        const oldAmount = transaction.type === 'income' 
          ? -transaction.amount 
          : transaction.amount;
        const newAmount = data.type === 'income' 
          ? data.amount 
          : -data.amount;
        balanceAfter = selectedAccount.current_balance + oldAmount + newAmount;
      } else {
        // For create: just apply new transaction
        balanceAfter = data.type === 'income'
          ? selectedAccount.current_balance + data.amount
          : selectedAccount.current_balance - data.amount;
      }

      // CRITICAL: Prevent negative balance
      if (balanceAfter < 0) {
        toast.error(
          'Solde insuffisant',
          `Cette opération rendrait le solde négatif (${balanceAfter.toLocaleString()} DA). Le solde minimum autorisé est 0 DA.`
        );
        return;
      }

      // Clean data: convert empty strings to undefined for optional date fields
      const cleanedData = {
        ...data,
        check_date: data.check_date || undefined,
        check_number: data.check_number || undefined,
        check_bank: data.check_bank || undefined,
        check_drawer_name: data.check_drawer_name || undefined,
        contact_id: data.contact_id || undefined,
        contact_name: contactName || undefined,
        notes: data.notes || undefined,
        category: data.category || undefined,
      };

      if (isEditMode) {
        // Update existing transaction
        await updateTransaction.mutateAsync({
          id: transaction.id,
          ...cleanedData,
          contact_type: data.contact_id ? 'client' : undefined,
        });
        
        toast.success('Transaction modifiée', 'La transaction a été mise à jour avec succès.');
      } else {
        // Create new transaction
        await createTransaction.mutateAsync({
          ...cleanedData,
          company_id: company?.id,
          transaction_number: generateTransactionNumber(),
          currency: 'DZD',
          created_by: user?.id,
          contact_type: data.contact_id ? 'client' : undefined,
          check_status: data.payment_method === 'check' ? 'pending' : undefined,
        });
        
        toast.success('Transaction créée', 'La transaction a été enregistrée avec succès.');
        
        // Clear cache on successful submit
        sessionStorage.removeItem(FORM_CACHE_KEY);
      }
      
      onClose();
      form.reset();
    } catch (error) {
      toast.error('Erreur', "Une erreur est survenue lors de l'enregistrement.");
      console.error('Error saving transaction:', error);
    }
  };

  const handleAddClient = () => {
    // Save current form state
    sessionStorage.setItem(FORM_CACHE_KEY, JSON.stringify(form.getValues()));
    // Navigate to add client page
    navigate({ to: '/apps/clients' });
  };

  // Update contact_name when client is selected
  useEffect(() => {
    if (contactId) {
      const client = clients.find(c => c.id === contactId);
      if (client) {
        form.setValue('contact_name', client.name);
      }
    }
  }, [contactId, clients]);

  const categories = transactionType === 'income' 
    ? TRANSACTION_CATEGORIES.income 
    : TRANSACTION_CATEGORIES.expense;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode 
              ? 'تعديل المعاملة / Modifier la transaction'
              : 'معاملة جديدة / Nouvelle transaction'
            }
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Transaction Type */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>نوع المعاملة / Type *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="income">
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-600">↗</span>
                          <span>دخل / Entrée (Recette)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="expense">
                        <div className="flex items-center gap-2">
                          <span className="text-red-600">↘</span>
                          <span>مصروف / Sortie (Dépense)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Account */}
            <FormField
              control={form.control}
              name="account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>الحساب / Compte *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez un compte" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accounts.length === 0 ? (
                        <div className="p-2 text-sm text-gray-500">
                          Aucun compte disponible
                        </div>
                      ) : (
                        accounts.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.name} ({account.current_balance.toLocaleString()} DA)
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Balance Preview */}
            {selectedAccount && amount > 0 && (
              <div className={`p-4 rounded-lg border-2 ${
                balancePreview < 0 
                  ? 'bg-red-50 border-red-300' 
                  : 'bg-blue-50 border-blue-300'
              }`}>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Solde actuel:</span>
                    <span className="font-semibold">
                      {selectedAccount.current_balance.toLocaleString()} DA
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {transactionType === 'income' ? 'Après entrée:' : 'Après sortie:'}
                    </span>
                    <span className={`font-bold ${
                      balancePreview < 0 ? 'text-red-600' : 'text-emerald-600'
                    }`}>
                      {balancePreview.toLocaleString()} DA
                    </span>
                  </div>
                  {balancePreview < 0 && (
                    <div className="flex items-start gap-2 mt-3 p-3 bg-red-100 rounded">
                      <span className="text-red-600 text-xl">⚠️</span>
                      <div className="text-sm text-red-800">
                        <p className="font-semibold">سالب / Solde négatif!</p>
                        <p className="mt-1">
                          Cette opération rendrait le solde négatif. 
                          Montant maximum autorisé: {selectedAccount.current_balance.toLocaleString()} DA
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Amount */}
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>المبلغ / Montant (DA) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Date */}
              <FormField
                control={form.control}
                name="transaction_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>التاريخ / Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Payment Method */}
            <FormField
              control={form.control}
              name="payment_method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>وسيلة الدفع / Moyen de paiement *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="cash">💵 نقد / Espèces</SelectItem>
                      <SelectItem value="check">🧾 شيك / Chèque</SelectItem>
                      <SelectItem value="transfer">🏦 تحويل بنكي / Virement bancaire</SelectItem>
                      <SelectItem value="card">💳 بطاقة / Carte bancaire</SelectItem>
                      <SelectItem value="ccp">📮 CCP</SelectItem>
                      <SelectItem value="mobile">📱 Mobile (Baridi Mob)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Check Fields */}
            {paymentMethod === 'check' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="check_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>رقم الشيك / N° chèque</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: 1234567" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="check_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>تاريخ الشيك / Date chèque</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="check_bank"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>البنك / Banque</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: BNA" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="check_drawer_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>صاحب الشيك / Tireur</FormLabel>
                      <FormControl>
                        <Input placeholder="Nom du tireur" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800">
                    ⚠️ تذكير: الشيكات صالحة لمدة 180 يوماً في الجزائر
                    <br />
                    Rappel: Les chèques sont valables 180 jours en Algérie
                  </p>
                </div>
              </>
            )}

            {/* Category */}
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>الفئة / Catégorie</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez une catégorie" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map((cat) => {
                        const label = CATEGORY_LABELS[cat];
                        return (
                          <SelectItem key={cat} value={cat}>
                            {label ? `${label.ar} / ${label.fr}` : cat}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>الوصف / Description *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Paiement client ABC" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Client Selection */}
            <FormField
              control={form.control}
              name="contact_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {transactionType === 'income' ? 'العميل / Client' : 'المورد / Fournisseur'}
                  </FormLabel>
                  <div className="flex gap-2">
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Sélectionnez un client..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <div className="p-2 border-b">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start"
                            onClick={handleAddClient}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter un client
                          </Button>
                        </div>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Manual Contact Name (if no client selected) */}
            {!contactId && (
              <FormField
                control={form.control}
                name="contact_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {transactionType === 'income' ? 'De / من' : 'À / إلى'}
                    </FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={transactionType === 'income' ? 'Nom du client' : 'Nom du fournisseur'} 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ملاحظات / Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Notes supplémentaires..."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={createTransaction.isPending}
              >
                إلغاء / Annuler
              </Button>
              <Button
                type="submit"
                disabled={createTransaction.isPending || updateTransaction.isPending || balancePreview < 0}
                className="bg-gradient-to-r from-emerald-500 to-teal-600"
              >
                {(createTransaction.isPending || updateTransaction.isPending) && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {isEditMode ? 'تحديث / Mettre à jour' : 'حفظ / Enregistrer'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
